/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  RadioGroup,
  IconButton,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Spinner } from '../atoms';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';

interface TaskCardData {
  id: number;
  reference_number: string;
  country: string;
  unit: {
    id: number;
    label: string;
  };
  nnc_date: string;
  product: string;
  production_order: string;
  operation: string;
  operator_name: string;
  machine: string;
  failure_category_description: string;
  description: string;
  status: {
    id: number;
    label: string;
  };
  created_at: string;
  modified_by: {
    id: number;
    label: string;
  };
  modified_at: string;
  has_critical: boolean;
  has_actionplan: boolean;
}

interface StatusIndicatorProps {
  active: boolean;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ active }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 12,
      height: 12,
      borderRadius: '50%',
      border: '1px solid #4caf50',
      backgroundColor: active ? '#4caf50' : '#FFF',
      marginRight: 1,
    }}
  />
);

interface TaskCardProps {
  task: TaskCardData;
}

const TaskCard: React.FC<TaskCardProps> = ({ task }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <Card
      onClick={() => navigate(`/nnc/nnc/add-update/${task.id}`)}
      sx={{
        borderRadius: 2,
        boxShadow: 3,
        mb: 2,
        overflow: 'visible',
        cursor: 'pointer',
        height: '100%',
        '&:hover': {
          scale: 1.01,
          transition: 'ease-in-out 0.1s',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor:
            task.status.id == 1
              ? 'primary.main'
              : task.status.id == 2
                ? '#2196f3'
                : 'primary.main',
          color: 'white',
          padding: 1.5,
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ color: '#FFF' }}
        >
          {task.reference_number}
        </Typography>
        {task.status.id == 2 && (
          <IconButton
            size="small"
            sx={{ color: 'white', border: '1px solid #FFF' }}
          >
            <EditIcon sx={{ color: '#fFF' }} />
          </IconButton>
        )}
      </Box>

      <CardContent sx={{ pt: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              {task.country}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {task.unit.label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon
              sx={{ color: '#4caf50', fontSize: 20, mr: 0.5 }}
            />
            <Typography variant="body2">{task.nnc_date}</Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('Product')}
        </Typography>
        <Typography
          variant="body1"
          color="primary"
          fontWeight={'bold'}
          sx={{ mb: 1 }}
        >
          {task.product}
        </Typography>

        <Typography variant="body2" fontWeight={'bold'} color="text.secondary">
          {t('Operation')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {task.operation}
        </Typography>

        <Typography variant="body2" fontWeight={'bold'} color="text.secondary">
          {t('Description')}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {task.description}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 6 }}>
            <Typography
              variant="body2"
              fontWeight={'bold'}
              color="text.secondary"
              mb={1}
            >
              Status SAP
            </Typography>
            <RadioGroup>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator active />
                <Typography variant="body2">{t('Ongoing Task')}</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator active={false} />
                <Typography variant="body2">{t('NNC Closed')}</Typography>
              </Box>
            </RadioGroup>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography
              variant="body2"
              fontWeight={'bold'}
              color="text.secondary"
            >
              Status PryData
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator active />
                <Typography variant="body2">{task.status.label}</Typography>
              </Box> */}

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator active={task.has_critical || false} />
                <Typography variant="body2">
                  {t('Critical Analysis')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator active={task.has_actionplan || false} />
                <Typography variant="body2">
                  {t('Are in Action Plan')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const CardsPage = ({
  data,
  isLoading,
}: {
  data: any[];
  isLoading: boolean;
}) => {
  return (
    <>
      <Spinner open={isLoading} />
      <Grid container spacing={2} mt={4}>
        {data.map((i: any) => (
          <Grid size={{ xs: 12, md: 4 }}>
            <TaskCard task={i} />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default CardsPage;
